<template>
  <div class="record-history">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="bi bi-clock-history me-2"></i>History Records
        </h5>
        <div class="d-flex gap-2">
          <button class="btn btn-sm btn-outline-primary" @click="$emit('export-data')">
            <i class="bi bi-download me-1"></i>Export CSV
          </button>
          <button class="btn btn-sm btn-outline-success" @click="$emit('email-data')">
            <i class="bi bi-envelope me-1"></i>Email CSV
          </button>
        </div>
      </div>
      <div class="card-body">
        <!-- Empty State -->
        <div v-if="records.length === 0" class="text-center py-5">
          <i class="bi bi-inbox display-1 text-muted"></i>
          <p class="text-muted mt-3">No records yet. Start tracking your cycle!</p>
        </div>

        <!-- Records List -->
        <div v-else class="records-list">
          <div
            v-for="record in sortedRecords"
            :key="record.id"
            class="record-item card mb-3"
          >
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div class="flex-grow-1">
                  <!-- Dates -->
                  <h6 class="mb-2">
                    <i class="bi bi-calendar-event text-danger me-2"></i>
                    {{ formatDate(record.start_date) }}
                    <span v-if="record.end_date" class="text-muted">
                      → {{ formatDate(record.end_date) }}
                    </span>
                  </h6>

                  <!-- Duration -->
                  <p class="text-muted small mb-2" v-if="record.end_date">
                    <i class="bi bi-hourglass-split me-1"></i>
                    {{ calculateDuration(record.start_date, record.end_date) }} days
                  </p>

                  <!-- Flow Level -->
                  <div class="mb-2">
                    <span class="badge" :class="getFlowBadgeClass(record.flow_level)">
                      {{ getFlowLabel(record.flow_level) }}
                    </span>
                  </div>

                  <!-- Symptoms -->
                  <div v-if="record.symptoms && record.symptoms.length > 0" class="mb-2">
                    <small class="text-muted">Symptoms:</small>
                    <div class="d-flex flex-wrap gap-1 mt-1">
                      <span
                        v-for="symptom in record.symptoms"
                        :key="symptom"
                        class="badge bg-secondary"
                      >
                        {{ symptom }}
                      </span>
                    </div>
                  </div>

                  <!-- Mood -->
                  <div v-if="record.mood" class="mb-2">
                    <small class="text-muted">Mood:</small>
                    <span class="ms-2">{{ getMoodEmoji(record.mood) }} {{ record.mood }}</span>
                  </div>

                  <!-- Note -->
                  <div v-if="record.note" class="mt-2">
                    <small class="text-muted">Note:</small>
                    <p class="mb-0 mt-1 text-secondary">{{ record.note }}</p>
                  </div>
                </div>

                <!-- Actions -->
                <div class="d-flex flex-column gap-2">
                  <button
                    class="btn btn-sm btn-outline-primary"
                    @click="$emit('edit-record', record)"
                    title="Edit"
                  >
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-danger"
                    @click="$emit('delete-record', record.id)"
                    title="Delete"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RecordHistory',
  props: {
    records: {
      type: Array,
      default: () => []
    }
  },
  emits: ['edit-record', 'delete-record', 'export-data', 'email-data'],
  computed: {
    sortedRecords() {
      return [...this.records].sort((a, b) => {
        const dateA = a.start_date?.toDate?.() || new Date(a.start_date)
        const dateB = b.start_date?.toDate?.() || new Date(b.start_date)
        return dateB - dateA
      })
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return ''
      const d = date.toDate ? date.toDate() : new Date(date)
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    calculateDuration(startDate, endDate) {
      if (!startDate || !endDate) return 0
      const start = startDate.toDate ? startDate.toDate() : new Date(startDate)
      const end = endDate.toDate ? endDate.toDate() : new Date(endDate)
      return Math.ceil((end - start) / (24 * 60 * 60 * 1000))
    },
    getFlowLabel(level) {
      const labels = {
        light: 'Light Flow',
        medium: 'Medium Flow',
        heavy: 'Heavy Flow'
      }
      return labels[level] || level
    },
    getFlowBadgeClass(level) {
      const classes = {
        light: 'bg-info',
        medium: 'bg-warning',
        heavy: 'bg-danger'
      }
      return classes[level] || 'bg-secondary'
    },
    getMoodEmoji(mood) {
      const emojis = {
        happy: '😊',
        neutral: '😐',
        sad: '😢',
        anxious: '😰',
        irritable: '😤'
      }
      return emojis[mood] || ''
    }
  }
}
</script>

<style scoped>
.record-item {
  transition: all 0.2s ease;
}

.record-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.records-list {
  max-height: 600px;
  overflow-y: auto;
}

.badge {
  font-size: 0.75rem;
  padding: 0.35rem 0.65rem;
}

@media (max-width: 768px) {
  .records-list {
    max-height: 400px;
  }
}
</style>
